import {
  Button,
  CircularProgress,
  FormControl,
  GridLegacy as Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ModalIOF } from '@old/ui/modal/modal';

import { validityInsurance } from '../selects-insurance-data';
import { useState } from 'react';
import { useGlobalStore } from '@old/global-store/use-store';
import { isValidEmail } from '@old/utils/helper';
import { ToastAction } from '@old/features/ui/toast/toast-action';

import { useService } from '@old/hooks/use-service';
import { brasilGenericQuoteEmailService } from '@old/services/integration/insurance/quote-email/brasil-generic-quote-email';
import i18n from '../../../libs/language';

type ServiceBody = Parameters<typeof brasilGenericQuoteEmailService>[0]['body'];

type ContactData = Pick<ServiceBody, 'organization_name' | 'fullname' | 'email' | 'phone'>;
type InsuranceData = Pick<
  ServiceBody,
  | 'sum_assured'
  | 'currency'
  | 'duration'
  | 'type'
  | 'firefighters'
  | 'theft_alerts'
  | 'survillance'
  | 'collateral_assets'
  | 'silobags'
  | 'total_weight'
>;
type BodyInsuranceForm = ContactData & InsuranceData;

type GenericInsuranceModalProps = {
  type: 'swissre';
  setOpen: (open: boolean) => void;
  setIsSend: (isSend: boolean) => void;
  setIsQuoting: (isQuoting: boolean) => void;
  silobagIds?: string[];
  vigency: number;
  totalSumAssured: number;
  tons: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  insurance: any;
};

export function BrInsuranceBodyModal(props: GenericInsuranceModalProps) {
  const { insurance, silobagIds, tons, vigency, totalSumAssured } = props;
  const { setOpen, setIsSend, setIsQuoting } = props;

  const { organizationName, userFullname, email } = useGlobalStore();

  const [formSubmitted, setFormSubmitted] = useState(false);
  // const [errorTracking, setErrorTracking] = useState<BodyInsuranceForm>({} as BodyInsuranceForm);
  const [toast, setToast] = useState({
    message: '',
    severity: 'error' as const,
    open: false,
  });

  const [insuranceForm, setInsuranceForm] = useState<BodyInsuranceForm>({
    duration:
      validityInsurance[props.type].find((ins) => ins.id === Math.floor(vigency / 3))?.description ?? '',
    currency: 'reais',
    sum_assured: totalSumAssured,
    organization_name: organizationName ? organizationName : 'Sin Organización',
    fullname: userFullname ? userFullname : '',
    email: email ? email : '',
    phone: '',
    silobags: silobagIds ?? [],
    collateral_assets: false,
    survillance: false,
    firefighters: false,
    theft_alerts: false,
    type: 'seguro novo',
    total_weight: tons * 1_000,
  });

  const { trigger: brasilQuoteEmailTrigger, isLoading: brasilQuoteEmailLoading } = useService(
    brasilGenericQuoteEmailService,
    {
      onSuccess: () => {
        setIsSend(true);
        setTimeout(() => {
          setInsuranceForm({
            duration:
              validityInsurance[props.type].find((ins) => ins.id === Math.floor(vigency / 3))?.description ??
              '',
            currency: 'reais',
            sum_assured: totalSumAssured,
            organization_name: organizationName ? organizationName : 'Sin Organización',
            fullname: userFullname ? userFullname : '',
            email: email ? email : '',
            phone: '',
            silobags: silobagIds ?? [],
            collateral_assets: false,
            survillance: false,
            firefighters: false,
            theft_alerts: false,
            type: 'seguro novo',
            total_weight: tons * 1_000,
          });
        }, 2000);
      },
      onError: () => {
        setToast({
          message: 'Erro ao enviar cotação. Por favor, tente novamente.',
          severity: 'error',
          open: true,
        });
      },
    }
  );
  const handleSubmitInsuranceBR = (
    event: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    setFormSubmitted(true);

    const hasEmptyRequiredField = Object.values(insuranceForm).some(
      (value) => value === '' || value === undefined
    );
    if (hasEmptyRequiredField) return;

    brasilQuoteEmailTrigger({ body: insuranceForm }, { type: props.type as 'swissre' });
  };

  const SACA_SIZE = 60; // kg
  const SACA_SIZE_IN_TONS = SACA_SIZE / 1000; // toneladas

  return (
    <ModalIOF.Body>
      {toast.open && (
        <ToastAction
          message={toast.message}
          severity={toast.severity}
          openToast={toast.open}
          setOpenToast={(open) => setToast({ ...toast, open })}
        />
      )}
      <Typography variant="h5" sx={{ fontWeight: 600, margin: '15px' }}>
        Complete o formulário para obter sua cotação
      </Typography>
      <Stack direction={'row'} spacing={2} sx={{ marginBottom: 3 }}>
        <Typography variant="body2" sx={{ color: '#9d9d9d' }}>
          Silos bolsa seleccionados:
          <span style={{ fontWeight: 600, color: 'black' }}>{silobagIds?.length}</span>
        </Typography>
        <Typography variant="body2" sx={{ color: '#9d9d9d' }}>
          Toneladas cotadas: <span style={{ fontWeight: 600, color: 'black' }}>{tons}</span>
        </Typography>
        <Typography variant="body2" sx={{ color: '#9d9d9d' }}>
          Sacas:{' '}
          <span style={{ fontWeight: 600, color: 'black' }}>{Math.round(tons / SACA_SIZE_IN_TONS)}</span>
        </Typography>
      </Stack>
      <Grid container spacing={2} sx={{ textAlign: 'center' }}>
        <Grid item xs={12} sm={4}>
          <FormControl variant="outlined" required size="small" style={{ width: '100%' }}>
            <InputLabel>Vigência</InputLabel>
            <Select
              style={{ width: '100%' }}
              onChange={(e) => {
                setInsuranceForm({ ...insuranceForm, duration: e.target.value as string });
              }}
              required
              input={<OutlinedInput label="Vigência" />}
              error={formSubmitted && !insuranceForm.duration}
              value={insuranceForm.duration}
            >
              {validityInsurance[props.type]?.map((item) => (
                <MenuItem key={item.id} value={item.description}>
                  {item.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl variant="outlined" required size="small" style={{ width: '100%' }}>
            <InputLabel>Moeda</InputLabel>
            <Select
              style={{ width: '100%' }}
              onChange={(e) => {
                setInsuranceForm({ ...insuranceForm, currency: e.target.value as string });
              }}
              required
              disabled
              input={<OutlinedInput label="Moeda" />}
              error={formSubmitted && !insuranceForm.currency}
              value={insuranceForm.currency}
            >
              <MenuItem value="reais">Reais</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl variant="outlined" required size="small" style={{ width: '100%' }}>
            <Tooltip
              title={
                insuranceForm.sum_assured != 0
                  ? ''
                  : 'O Total segurado é o valor dos grãos armazenados no silobolsa. É calculado como a quantidade de toneladas multiplicada pelo preço do grão.'
              }
            >
              <TextField
                style={{ width: '100%' }}
                size="small"
                required
                label="Total segurado"
                type="number"
                value={insuranceForm.sum_assured}
                onChange={(e) => {
                  setInsuranceForm({
                    ...insuranceForm,
                    sum_assured: e.target.value ? parseInt(e.target.value) : 0,
                  });
                }}
                error={formSubmitted && !insuranceForm.sum_assured}
              />
            </Tooltip>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl variant="outlined" required size="small" style={{ width: '100%' }}>
            <InputLabel>Tipo de Seguro</InputLabel>
            <Select
              required
              style={{ width: '100%' }}
              input={<OutlinedInput label="Tipo de Seguro" />}
              value={insuranceForm.type}
              onChange={(e) => {
                setInsuranceForm({ ...insuranceForm, type: e.target.value as string });
              }}
              error={formSubmitted && !insuranceForm.type}
            >
              <MenuItem value="seguro novo">Seguro novo</MenuItem>
              <MenuItem value="renov. outra seg.">Renov. Outra Seg.</MenuItem>
              <MenuItem value="renovação swiss re">Renovação Swiss Re</MenuItem>
              <MenuItem value="renov. swiss re c/ sinistro">Renov. Swiss Re c/ Sinistro</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl variant="outlined" required size="small" style={{ width: '100%' }}>
            <InputLabel>Ativo(s) cedido(s) em garantia?</InputLabel>
            <Select
              required
              style={{ width: '100%' }}
              input={<OutlinedInput label="Ativo(s) cedido(s) em garantia?" />}
              value={insuranceForm.collateral_assets ? 1 : 0}
              onChange={(e) => {
                setInsuranceForm({ ...insuranceForm, collateral_assets: Boolean(Number(e.target.value)) });
              }}
              error={formSubmitted && insuranceForm.collateral_assets == undefined}
            >
              <MenuItem value={1}>{i18n.t('app.yes')}</MenuItem>
              <MenuItem value={0}>{i18n.t('app.no')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl variant="outlined" required size="small" style={{ width: '100%' }}>
            <InputLabel>Possui vigilância 24h?</InputLabel>
            <Select
              required
              style={{ width: '100%' }}
              input={<OutlinedInput label="Possui vigilância 24h?" />}
              value={insuranceForm.survillance ? 1 : 0}
              onChange={(e) => {
                setInsuranceForm({ ...insuranceForm, survillance: Boolean(Number(e.target.value)) });
              }}
              error={formSubmitted && insuranceForm.survillance == undefined}
            >
              <MenuItem value={1}>{i18n.t('app.yes')}</MenuItem>
              <MenuItem value={0}>{i18n.t('app.no')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl variant="outlined" required size="small" style={{ width: '100%' }}>
            <InputLabel>Possui brigada de incêndio?</InputLabel>
            <Select
              required
              style={{ width: '100%' }}
              input={<OutlinedInput label="Possui brigada de incêndio?" />}
              value={insuranceForm.firefighters ? 1 : 0}
              onChange={(e) => {
                setInsuranceForm({ ...insuranceForm, firefighters: Boolean(Number(e.target.value)) });
              }}
              error={formSubmitted && insuranceForm.firefighters == undefined}
            >
              <MenuItem value={1}>{i18n.t('app.yes')}</MenuItem>
              <MenuItem value={0}>{i18n.t('app.no')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl variant="outlined" required size="small" style={{ width: '100%' }}>
            <InputLabel>Alarme contra roubo/furto?</InputLabel>
            <Select
              required
              style={{ width: '100%' }}
              input={<OutlinedInput label="Possui sistema de alarme contra roubo e furto?" />}
              value={insuranceForm.theft_alerts ? 1 : 0}
              onChange={(e) => {
                setInsuranceForm({ ...insuranceForm, theft_alerts: Boolean(Number(e.target.value)) });
              }}
              error={formSubmitted && insuranceForm.theft_alerts == undefined}
            >
              <MenuItem value={1}>{i18n.t('app.yes')}</MenuItem>
              <MenuItem value={0}>{i18n.t('app.no')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Typography sx={{ margin: '10px', fontWeight: 600 }}>Seus dados de contato</Typography>
      <TextField
        size="small"
        sx={{ width: '100%' }}
        required
        label="Organização"
        value={organizationName}
        disabled
        // error={formSubmitted && !insuranceForm.organization_name}
      />
      <Grid container sx={{ marginTop: '15px' }} spacing={2}>
        <Grid item xs={12} sm={4}>
          <FormControl variant="outlined" size="small" style={{ width: '100%' }}>
            <TextField
              style={{ width: '100%' }}
              size="small"
              required
              label="Nome e sobrenome"
              value={insuranceForm.fullname}
              onChange={(e) => {
                setInsuranceForm({ ...insuranceForm, fullname: e.target.value });
              }}
              error={formSubmitted && !insuranceForm.fullname}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControl variant="outlined" size="small" style={{ width: '100%' }}>
            <TextField
              style={{ width: '100%' }}
              size="small"
              required
              label="E-mail"
              value={insuranceForm.email}
              onChange={(e) => {
                setInsuranceForm({ ...insuranceForm, email: e.target.value });
              }}
              error={
                (formSubmitted && !insuranceForm.email) ||
                (insuranceForm.email ? !isValidEmail(insuranceForm.email) : false)
              }
              type="email"
              helperText={
                isValidEmail(insuranceForm.email) || insuranceForm.email == '' ? '' : 'E-mail inválido'
              }
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControl variant="outlined" size="small" style={{ width: '100%' }}>
            <TextField
              style={{ width: '100%' }}
              size="small"
              required
              label="Celular"
              onChange={(e) => {
                setInsuranceForm({ ...insuranceForm, phone: e.target.value });
              }}
              error={formSubmitted && !insuranceForm.phone}
            />
            <p style={{ fontSize: '12px', margin: '0', textAlign: 'left', color: '#A7A6A6' }}>
              Exemplo: +5491157059192
            </p>
          </FormControl>
        </Grid>
      </Grid>
      <Stack direction="row" spacing={2} justifyContent={'right'} sx={{ marginTop: '15px' }}>
        <Button
          color="error"
          onClick={() => {
            setIsQuoting(false);
            setOpen(false);
          }}
          sx={{ color: 'black' }}
        >
          Fechar
        </Button>
        <Button
          variant="contained"
          sx={{
            color: '#FFF',
            fontSize: 14,
            borderRadius: 100,
            textTransform: 'none',
            padding: '0px 30px',
            margin: '10px',
            ...insurance.callToActionStyles,
          }}
          onClick={handleSubmitInsuranceBR}
        >
          {brasilQuoteEmailLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Enviar'}
        </Button>
      </Stack>
    </ModalIOF.Body>
  );
}
